import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO/meta block. Drop at top of any page component.
 *
 *   <SEO
 *     title="Custom T-Shirts Toronto"
 *     description="…"
 *     path="/customize"
 *     image="/og.png"
 *   />
 */
const SITE_NAME = "Toronto Custom T-Shirts";
const SITE_URL = "https://torontocustomtshirts.ca";
const DEFAULT_IMG = "https://torontocustomtshirts.ca/logo.png";

export default function SEO({ title, description, path = "", image, keywords, noindex = false, schema = null }) {
  // Bing flags titles >60 chars as "Title too long" (Warning). For long
  // product names (e.g. "Marvel Comics Collage Hoodie & T-Shirt – Colorful
  // Superhero Graphic Sweatshirt") we'd previously append " | Toronto
  // Custom T-Shirts" (28 chars) and blow well past 60. Strategy:
  //   - If `title + suffix` fits in 60 chars → keep the brand suffix.
  //   - Otherwise drop the suffix entirely (the title alone is more
  //     keyword-dense and ranking-relevant).
  // iter47o SEO audit (Bing Webmaster Tools, Feb 2026).
  const MAX_TITLE = 60;
  const suffix = ` | ${SITE_NAME}`;
  let fullTitle;
  if (!title) {
    fullTitle = SITE_NAME;
  } else if (title.length + suffix.length <= MAX_TITLE) {
    fullTitle = `${title}${suffix}`;
  } else {
    // Long titles: keep them as-is but cap at 60 chars with an ellipsis
    // so search-engine listings stay clean.
    fullTitle = title.length <= MAX_TITLE ? title : title.slice(0, MAX_TITLE - 1).trimEnd() + "…";
  }
  // Bing also warns when meta description exceeds 160 chars. Truncate
  // softly so the page still has a sensible snippet.
  const MAX_DESC = 160;
  const desc = description && description.length > MAX_DESC
    ? description.slice(0, MAX_DESC - 1).trimEnd() + "…"
    : description;
  const url = `${SITE_URL}${path}`;
  // Social platforms (Facebook, LinkedIn, iMessage, Twitter) require
  // ABSOLUTE og:image URLs. Product images on this app are served as
  // relative paths like `/api/static/imported/abc.jpg` — promote them
  // to absolute against the production canonical so previews render
  // correctly when shared from any environment.
  const rawImg = image || DEFAULT_IMG;
  const img = rawImg.startsWith("http") ? rawImg : `${SITE_URL}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_CA" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {desc && <meta name="twitter:description" content={desc} />}
      <meta name="twitter:image" content={img} />
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}
